import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
    PiArrowLeftBold,
    PiArrowSquareOutBold,
    PiCaretDownBold,
    PiCheckCircleFill,
    PiLockSimpleBold,
    PiSlidersHorizontalBold,
    PiMagnifyingGlassBold,
    PiMapPinBold,
    PiPackageBold,
    PiTruckBold,
    PiWarehouseBold,
} from 'react-icons/pi';
import { Badge, Button, Input, LoadingState, PageHeader, Skeleton, Title } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import ProductThumbnail from '@v2/features/products/ProductThumbnail';
import { StockPhaseBadge, StockShipmentLink } from '@v2/features/products/StockPhaseBadge';
import { DOT_STYLE, dotInlineStyle } from '@v2/features/colors/ColorDot';
import CdLocationHighlightModal from '@v2/features/cd-rack/CdLocationHighlightModal';
import StockAiSuggestionButton from '@v2/features/ai/StockAiSuggestionButton';

type DealOption = {
    id: number;
    internal_number: string | null;
    deal_number: string | null;
    title: string;
    contact_name: string | null;
    owner_name: string | null;
    status_label: string;
    matching_order_product_id: number | null;
    matching_line_qty: number | null;
    /** Cor vendida na linha (frame|seat|stitches|arm) — pré-seleciona o card certo. */
    matching_line_color_key?: string | null;
    /** false = venda ainda sem pedido no Ploomes (não dá para reservar). */
    has_order?: boolean;
};

/** Quais vendas a lista mostra: as do vendedor, da equipe do gerente ou todas. */
type DealScope = 'own' | 'team' | 'all';

type ColorSwatch = {
    category: string;
    name: string;
    /** null = nome fora do padrão (sem correspondência no cadastro nem no de-para). */
    hex: string | null;
};

type StockUnit = {
    id: number;
    serial_number: string | null;
    status: string;
    stock_phase?: 'terra' | 'transito' | string | null;
    stock_phase_label?: string | null;
    shipment_id?: number | null;
    shipment_label?: string | null;
    shelf_code: string | null;
    eta?: string | null;
    eta_label?: string | null;
    cd_arrived_at: string | null;
    days_at_cd: number;
    billable_days: number;
    cbm: number | null;
    total_charge_brl: number;
    is_billable: boolean;
    charge_reason: string | null;
};

type ColorCard = {
    key: string;
    label: string;
    swatches: ColorSwatch[];
    available: number;
    reserved: number;
    in_transit: number;
    shelves: string[];
    max_days_at_cd: number;
    billable_units: number;
    total_charge_brl: number;
    next_arrival_label?: string | null;
    matches_color?: boolean;
    units?: StockUnit[];
};

type MachineComponent = {
    category: string;
    customizable: boolean;
    options_count: number;
    stock_colors: { name: string; hex: string | null }[];
};

type StockConsultProduct = {
    id: number;
    code: string | null;
    name: string;
    image_url: string | null;
    product_type_label: string | null;
    category: string | null;
    series: string | null;
    is_high_turnover?: boolean;
    cbm: number | null;
    storage_policy: {
        free_days: number;
        daily_rate_brl: number;
        distribution_center: string | null;
    };
    totals: {
        available: number;
        reserved: number;
        in_transit: number;
        total: number;
        max_days_at_cd: number;
        total_charge_brl: number;
    };
    color_cards: ColorCard[];
    components?: MachineComponent[];
    /** Rua/prateleira e mapa do CD só para logística/comex. */
    can_view_cd_location?: boolean;
};

const formatBrl = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(timer);
    }, [value, delayMs]);
    return debounced;
}

function partLabel(category: string, t: (key: string) => string): string {
    switch (category) {
        case 'frame':
            return t('v2.stock_consult.part_frame');
        case 'seat':
            return t('v2.stock_consult.part_seat');
        case 'stitches':
            return t('v2.stock_consult.part_stitches');
        case 'arm':
            return t('v2.stock_consult.part_arm');
        default:
            return t('v2.stock_consult.no_color');
    }
}

const PART_ORDER = ['frame', 'seat', 'stitches', 'arm'];

function ColorDotName({ name, hex, size = 'sm' }: { name: string; hex: string | null; size?: 'sm' | 'lg' }) {
    return (
        <span className="flex min-w-0 items-center gap-2" title={`${name}${hex ? '' : ' (fora do padrão)'}`}>
            <span
                className={cn(
                    'shrink-0 rounded-full',
                    size === 'lg' ? 'size-7' : 'size-4',
                    hex ? 'border border-black/10 dark:border-white/15' : DOT_STYLE.off,
                )}
                style={dotInlineStyle(hex ? 'standard' : 'off', hex)}
            />
            <span className="truncate text-xs font-medium text-gray-800 dark:text-gray-100">{name}</span>
        </span>
    );
}

function MachineComponents({
    components,
    t,
}: {
    components: MachineComponent[];
    t: (key: string, vars?: Record<string, string | number>) => string;
}) {
    if (components.length === 0) return null;

    return (
        <section>
            <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                {t('v2.stock_consult.components_title')}
            </h3>
            <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                {components.map((component) => {
                    const colors = component.stock_colors;
                    return (
                        <div
                            key={component.category}
                            className="flex flex-col gap-2.5 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800"
                        >
                            <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                                {partLabel(component.category, t)}
                            </p>
                            {colors.length === 1 ? (
                                <ColorDotName name={colors[0].name} hex={colors[0].hex} size="lg" />
                            ) : colors.length > 1 ? (
                                <div className="flex items-center gap-2">
                                    <span className="flex -space-x-1.5">
                                        {colors.slice(0, 5).map((c) => (
                                            <span
                                                key={c.name}
                                                title={c.name}
                                                className={cn(
                                                    'size-7 rounded-full border-2 border-white dark:border-gray-800',
                                                    !c.hex && DOT_STYLE.off,
                                                )}
                                                style={dotInlineStyle(c.hex ? 'standard' : 'off', c.hex)}
                                            />
                                        ))}
                                    </span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {t('v2.stock_consult.colors_in_stock', { count: colors.length })}
                                    </span>
                                </div>
                            ) : (
                                <span className="text-xs text-gray-400">{t('v2.stock_consult.no_stock_color')}</span>
                            )}
                            <p
                                className={cn(
                                    'mt-auto flex items-center gap-1.5 text-[11px] font-medium',
                                    component.customizable ? 'text-violet-600 dark:text-violet-300' : 'text-gray-500 dark:text-gray-400',
                                )}
                            >
                                {component.customizable ? (
                                    <>
                                        <PiSlidersHorizontalBold className="size-3.5" />
                                        {t('v2.stock_consult.customizable', { count: component.options_count })}
                                    </>
                                ) : (
                                    <>
                                        <PiLockSimpleBold className="size-3.5" />
                                        {t('v2.stock_consult.fixed')}
                                    </>
                                )}
                            </p>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function CombinationRow({
    card,
    index,
    parts,
    t,
    onViewMap,
    canViewLocation,
    onReserve,
    gridCols,
}: {
    card: ColorCard;
    index: number;
    parts: string[];
    t: (key: string, vars?: Record<string, string | number>) => string;
    onViewMap?: (shelves: string[]) => void;
    canViewLocation: boolean;
    onReserve?: (colorKey: string) => void;
    gridCols: string;
}) {
    const units = card.units ?? [];
    const [unitsOpen, setUnitsOpen] = useState(false);
    const canOpenMap = Boolean(canViewLocation && onViewMap && card.shelves.length > 0);
    const shelvesLabel =
        card.shelves.length > 0 ? card.shelves.slice(0, 3).join(', ') + (card.shelves.length > 3 ? '…' : '') : null;

    return (
        <div
            className={cn(
                'rounded-xl border bg-white transition dark:bg-gray-800',
                unitsOpen || card.matches_color === true
                    ? 'border-primary/50 bg-primary/[0.03] ring-1 ring-primary/20'
                    : 'border-gray-200 dark:border-gray-700',
                card.matches_color === false && 'opacity-50',
            )}
        >
            <div
                role="button"
                tabIndex={0}
                onClick={() => units.length > 0 && setUnitsOpen((v) => !v)}
                onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && units.length > 0) {
                        e.preventDefault();
                        setUnitsOpen((v) => !v);
                    }
                }}
                aria-expanded={unitsOpen}
                className="grid cursor-pointer items-center gap-x-3 gap-y-1 px-3 py-2.5"
                style={{ gridTemplateColumns: gridCols }}
            >
                <span className="flex items-center gap-1 font-mono text-xs font-semibold text-gray-500">
                    <PiCaretDownBold className={cn('size-3 transition-transform', !unitsOpen && '-rotate-90')} />
                    {String(index + 1).padStart(2, '0')}
                </span>
                {parts.map((part) => {
                    const swatch = card.swatches.find((s) => s.category === part);
                    return (
                        <span key={part} className="min-w-0">
                            {swatch ? (
                                <ColorDotName name={swatch.name} hex={swatch.hex} />
                            ) : (
                                <span className="text-xs text-gray-300">—</span>
                            )}
                        </span>
                    );
                })}
                <span className="text-end">
                    <span
                        className={cn(
                            'text-sm font-bold tabular-nums',
                            card.available > 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-gray-300 dark:text-gray-600',
                        )}
                    >
                        {t('v2.stock_consult.units', { qty: card.available })}
                    </span>
                    {(card.reserved > 0 || card.in_transit > 0) && (
                        <span className="block text-[10px] leading-tight text-gray-500 dark:text-gray-400">
                            {[
                                card.reserved > 0 ? `${card.reserved} ${t('v2.stock_consult.reserved').toLowerCase()}` : null,
                                card.in_transit > 0 ? `${card.in_transit} ${t('v2.stock_consult.in_transit').toLowerCase()}` : null,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </span>
                    )}
                </span>
                <span className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {canOpenMap && (
                        <Button
                            variant="outline"
                            size="sm"
                            title={t('v2.stock_consult.view_on_map')}
                            aria-label={t('v2.stock_consult.view_on_map')}
                            onClick={() => onViewMap?.(card.shelves)}
                        >
                            <PiMapPinBold className="size-4" />
                        </Button>
                    )}
                    {onReserve && card.available > 0 && (
                        <Button size="sm" onClick={() => onReserve(card.key)}>
                            {t('v2.stock_consult.reserve')}
                        </Button>
                    )}
                </span>
            </div>

            {unitsOpen && units.length > 0 && (
                <div className="border-t border-gray-100 dark:border-gray-700">
                    <p className="flex flex-wrap gap-x-3 px-3 pt-2 text-[11px] text-gray-500 dark:text-gray-400">
                        {canViewLocation && (
                            <span className="font-mono font-semibold text-gray-700 dark:text-gray-200">
                                {shelvesLabel ?? t('v2.stock_consult.no_location')}
                            </span>
                        )}
                        {card.max_days_at_cd > 0 && <span>{t('v2.stock_consult.days_at_cd_max', { days: card.max_days_at_cd })}</span>}
                        {card.total_charge_brl > 0 && (
                            <span className="font-semibold text-rose-700 dark:text-rose-300">{formatBrl(card.total_charge_brl)}</span>
                        )}
                    </p>
                        <ul className="divide-y divide-gray-100 px-3 pb-2 dark:divide-gray-700/60">
                            {units.map((unit) => {
                                const clickable = Boolean(canViewLocation && unit.shelf_code && onViewMap);
                                return (
                                    <li
                                        key={unit.id}
                                        className={cn(
                                            'flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-xs',
                                            clickable && 'cursor-pointer rounded hover:bg-amber-50 dark:hover:bg-amber-950/30',
                                        )}
                                        onClick={clickable ? () => onViewMap!([unit.shelf_code!]) : undefined}
                                        title={
                                            clickable
                                                ? t('v2.stock_consult.view_shelf_on_map', { shelf: unit.shelf_code! })
                                                : undefined
                                        }
                                    >
                                        <span className="min-w-0 max-w-[10rem] truncate font-mono text-[11px] text-gray-800 dark:text-gray-100">
                                            {unit.serial_number ?? '—'}
                                        </span>
                                        {unit.status === 'reservado' ? (
                                            <span className="inline-flex items-center rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700 dark:bg-violet-500/15 dark:text-violet-300">
                                                {t('v2.stock_consult.unit_reserved')}
                                            </span>
                                        ) : (
                                            <StockPhaseBadge
                                                phase={unit.stock_phase ?? (unit.status === 'em_transito' ? 'transito' : 'terra')}
                                                label={unit.stock_phase_label}
                                            />
                                        )}
                                        {unit.shipment_id && (
                                            <StockShipmentLink shipmentId={unit.shipment_id} label={unit.shipment_label} />
                                        )}
                                        <span className="ms-auto flex items-center gap-3 tabular-nums text-gray-500 dark:text-gray-400">
                                            {canViewLocation && unit.shelf_code && (
                                                <span className="font-mono font-semibold text-amber-800 dark:text-amber-200">
                                                    {unit.shelf_code}
                                                </span>
                                            )}
                                            <span>
                                                {unit.status === 'em_transito'
                                                    ? unit.eta_label
                                                        ? t('v2.stock_consult.arrives_on', { date: unit.eta_label })
                                                        : '—'
                                                    : t('v2.stock_consult.days_at_cd', { days: unit.days_at_cd })}
                                            </span>
                                            {unit.total_charge_brl > 0 && (
                                                <span
                                                    className="font-semibold text-rose-700 dark:text-rose-300"
                                                    title={unit.charge_reason ?? undefined}
                                                >
                                                    {formatBrl(unit.total_charge_brl)}
                                                </span>
                                            )}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                </div>
            )}
        </div>
    );
}

function StockCombinations({
    cards,
    t,
    onViewMap,
    canViewLocation,
    onReserve,
}: {
    cards: ColorCard[];
    t: (key: string, vars?: Record<string, string | number>) => string;
    onViewMap?: (shelves: string[]) => void;
    canViewLocation: boolean;
    onReserve?: (colorKey: string) => void;
}) {
    const parts = PART_ORDER.filter((p) => cards.some((c) => c.swatches.some((s) => s.category === p)));
    const gridCols = `3.25rem repeat(${Math.max(parts.length, 1)}, minmax(0, 1fr)) 6.5rem ${onReserve ? '7.5rem' : '2.5rem'}`;

    return (
        <div className="space-y-1.5">
            <div
                className="grid gap-x-3 px-3 text-[10px] font-semibold uppercase tracking-wide text-gray-400"
                style={{ gridTemplateColumns: gridCols }}
            >
                <span>{t('v2.stock_consult.combination')}</span>
                {parts.length === 0 && <span>{t('v2.stock_consult.no_color')}</span>}
                {parts.map((part) => (
                    <span key={part} className="truncate">
                        {partLabel(part, t)}
                    </span>
                ))}
                <span className="text-end">{t('v2.stock_consult.available')}</span>
                <span />
            </div>
            {cards.map((card, index) => (
                <CombinationRow
                    key={card.key}
                    card={card}
                    index={index}
                    parts={parts.length > 0 ? parts : []}
                    t={t}
                    onViewMap={onViewMap}
                    canViewLocation={canViewLocation}
                    onReserve={onReserve}
                    gridCols={gridCols}
                />
            ))}
        </div>
    );
}

function AvailableProductCard({
    product,
    onSelect,
    t,
}: {
    product: StockConsultProduct;
    onSelect: () => void;
    t: (key: string, vars?: Record<string, string | number>) => string;
}) {
    const availableColors = product.color_cards.filter((c) => c.available > 0);
    const swatches = availableColors.flatMap((c) => c.swatches).slice(0, 5);

    return (
        <button
            type="button"
            onClick={onSelect}
            className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white text-left shadow-sm transition hover:border-emerald-300 hover:shadow-md dark:border-gray-700 dark:bg-gray-800 dark:hover:border-emerald-700"
        >
            <div className="relative border-b border-gray-100 dark:border-gray-700">
                <ProductThumbnail src={product.image_url} alt={product.name} size="grid" />
                <span className="absolute left-2 top-2">
                    <Badge color="success" variant="flat" rounded="pill" size="sm">
                        <PiWarehouseBold className="me-1 inline size-3" />
                        {t('v2.stock_consult.available_units', { qty: product.totals.available })}
                    </Badge>
                </span>
            </div>

            <div className="flex flex-1 flex-col gap-2 p-4">
                <div>
                    <p className="line-clamp-2 text-sm font-semibold text-gray-900 group-hover:text-primary dark:text-gray-100">
                        {product.name}
                    </p>
                    <p className="mt-1 font-mono text-[11px] text-gray-500">{product.code ?? '—'}</p>
                </div>

                {swatches.length > 0 && (
                    <div className="flex items-center gap-2">
                        <div className="flex -space-x-1.5">
                            {swatches.map((swatch, idx) => (
                                <span
                                    key={`${product.id}-${swatch.category}-${swatch.name}-${idx}`}
                                    title={`${swatch.name}${swatch.hex ? '' : ' (fora do padrão)'}`}
                                    className={cn(
                                        'size-5 rounded-full border-2 border-white dark:border-gray-800',
                                        !swatch.hex && DOT_STYLE.off,
                                    )}
                                    style={dotInlineStyle(swatch.hex ? 'standard' : 'off', swatch.hex)}
                                />
                            ))}
                        </div>
                        <span className="text-[11px] text-gray-500">
                            {t('v2.stock_consult.colors_with_stock', { count: availableColors.length })}
                        </span>
                    </div>
                )}

                <div className="mt-auto flex flex-wrap gap-1 pt-1">
                    {product.category && (
                        <Badge color="info" size="sm" rounded="pill" variant="flat">
                            {product.category}
                        </Badge>
                    )}
                    {product.series && (
                        <Badge color="warning" size="sm" rounded="pill" variant="flat">
                            {product.series}
                        </Badge>
                    )}
                </div>
            </div>
        </button>
    );
}

const sameColorKey = (a?: string | null, b?: string | null) =>
    (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

const DEALS_VISIBLE = 6;

function ReserveFromConsultModal({
    open,
    onClose,
    product,
    t,
    initialColorKey = null,
}: {
    open: boolean;
    onClose: () => void;
    product: StockConsultProduct;
    t: (key: string, vars?: Record<string, string | number>) => string;
    initialColorKey?: string | null;
}) {
    const [dealSearch, setDealSearch] = useState('');
    const [selectedDealId, setSelectedDealId] = useState<number | null>(null);
    const [qty, setQty] = useState(1);
    const [reason, setReason] = useState('');
    const debouncedDealSearch = useDebouncedValue(dealSearch.trim(), 300);
    // Reserva separa a cor escolhida — antes pegava qualquer unidade do produto.
    const colorCards = product.color_cards.filter((c) => c.available > 0);
    const [colorKey, setColorKey] = useState<string | null>(initialColorKey);
    const [showAllColors, setShowAllColors] = useState(false);

    useEffect(() => {
        if (!open) return;
        setDealSearch('');
        setSelectedDealId(null);
        setQty(1);
        setReason('');
        setShowAllColors(false);
        setColorKey(initialColorKey ?? (colorCards.length === 1 ? colorCards[0].key : null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, product.id, initialColorKey]);

    const dealsQuery = useQuery({
        queryKey: ['stock-request-deal-options', product.id, debouncedDealSearch],
        enabled: open,
        queryFn: async () => {
            const { data } = await api.get<{ data: DealOption[]; meta?: { scope?: DealScope } }>(
                '/stock-requests/deal-options',
                {
                    params: {
                        search: debouncedDealSearch || undefined,
                        product_id: product.id,
                    },
                },
            );
            return { deals: data.data, scope: data.meta?.scope ?? 'all' };
        },
    });

    const deals = dealsQuery.data?.deals ?? [];
    const scope = dealsQuery.data?.scope ?? 'all';
    const visibleDeals = deals.slice(0, DEALS_VISIBLE);
    const hiddenDeals = deals.length - visibleDeals.length;
    const selectedDeal = deals.find((d) => d.id === selectedDealId) ?? null;
    const selectedCard = colorCards.find((c) => sameColorKey(c.key, colorKey)) ?? null;
    const poolAvailable = selectedCard ? selectedCard.available : product.totals.available;
    const lineColorCard = selectedDeal?.matching_line_color_key
        ? colorCards.find((c) => sameColorKey(c.key, selectedDeal.matching_line_color_key))
        : undefined;
    const lineHasColor = Boolean(selectedDeal?.matching_line_color_key?.replace(/\|/g, '').trim());
    // Com a venda escolhida, só a cor do pedido aparece; as outras ficam atrás de "ver outras cores".
    const filterToLine = Boolean(lineColorCard) && !showAllColors;
    const visibleColorCards = filterToLine && lineColorCard ? [lineColorCard] : colorCards;
    const otherColorsCount = colorCards.length - 1;
    const soldColorOutOfStock = Boolean(selectedDeal && lineHasColor && !lineColorCard);
    const colorDiffersFromLine = Boolean(
        selectedDeal?.matching_line_color_key &&
            selectedDeal.matching_line_color_key.replace(/\|/g, '') !== '' &&
            colorKey !== null &&
            !sameColorKey(colorKey, selectedDeal.matching_line_color_key),
    );
    const maxQty = Math.max(1, Math.min(selectedDeal?.matching_line_qty ?? poolAvailable, poolAvailable));

    useEffect(() => {
        if (qty > maxQty) setQty(maxQty);
    }, [maxQty, qty]);

    const queryClient = useQueryClient();
    const reserveMut = useMutation({
        mutationFn: async () => {
            if (!selectedDeal?.matching_order_product_id) {
                throw new Error('missing_order_product');
            }
            const { data } = await api.post(`/ploomes-deals/${selectedDeal.id}/stock-requests`, {
                ploomes_order_product_id: selectedDeal.matching_order_product_id,
                qty,
                reason: reason.trim(),
                source: 'cd',
                color_key: colorKey,
            });
            return data;
        },
        onSuccess: (data) => {
            toast.success(data?.message ?? t('v2.stock_consult.reserve_success'));
            // Disponível / reservado mudam na hora: senão o card segue mostrando o saldo antigo.
            void queryClient.invalidateQueries({ queryKey: ['stock-consult'] });
            void queryClient.invalidateQueries({ queryKey: ['stock-requests'] });
            onClose();
        },
        onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
            toast.error(err?.response?.data?.message ?? t('v2.stock_consult.reserve_error'));
        },
    });

    const missingStep = !selectedDeal
        ? t('v2.stock_consult.missing_deal')
        : colorCards.length > 0 && colorKey === null
          ? t('v2.stock_consult.missing_color')
          : reason.trim().length < 3
            ? t('v2.stock_consult.missing_reason')
            : null;
    const canSubmit =
        Boolean(selectedDeal?.matching_order_product_id) && missingStep === null && qty >= 1 && qty <= maxQty;

    const stepTitle = (n: number, label: string) => (
        <p className="mb-2 flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-200">
            <span className="flex size-5 items-center justify-center rounded-full bg-gray-900 text-[10px] font-bold text-white dark:bg-gray-100 dark:text-gray-900">
                {n}
            </span>
            {label}
        </p>
    );

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title={t('v2.stock_consult.reserve_title')}
            description={`${product.name}${product.code ? ` · ${product.code}` : ''}`}
            size="2xl"
            footer={
                <>
                    {missingStep && <span className="me-auto text-xs text-gray-500">{missingStep}</span>}
                    <Button variant="outline" onClick={onClose}>
                        {t('common.close')}
                    </Button>
                    <Button
                        disabled={!canSubmit}
                        isLoading={reserveMut.isPending}
                        onClick={() => reserveMut.mutate()}
                    >
                        <PiWarehouseBold className="me-1.5 size-4" />
                        {t('v2.stock_consult.reserve_submit')}
                    </Button>
                </>
            }
        >
            {product.is_high_turnover && (
                <p className="mb-4 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-[11px] text-sky-800 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-200">
                    {t('v2.stock_consult.high_turnover_warning')}
                </p>
            )}
            <div className="grid gap-5 md:grid-cols-2">
                <section className="min-w-0">
                    {stepTitle(1, t('v2.stock_consult.select_open_deal'))}
                    <div className="relative">
                        <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                        <Input
                            value={dealSearch}
                            onChange={(e) => setDealSearch(e.target.value)}
                            placeholder={t('v2.stock_consult.deal_search_placeholder')}
                            className="ps-9"
                        />
                    </div>
                    <p className="mt-1.5 text-[11px] text-gray-400">{t(`v2.stock_consult.deal_scope_${scope}`)}</p>
                    <div className="mt-2 space-y-1.5">
                        {dealsQuery.isPending && <Skeleton className="h-14 w-full rounded-lg" />}
                        {dealsQuery.isError && (
                            <button
                                type="button"
                                onClick={() => void dealsQuery.refetch()}
                                className="text-xs font-medium text-rose-600 hover:underline"
                            >
                                {t('v2.stock_consult.load_error')} · {t('v2.stock_consult.retry')}
                            </button>
                        )}
                        {!dealsQuery.isPending && !dealsQuery.isError && deals.length === 0 && (
                            <p className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-400 dark:border-zinc-700">
                                {t('v2.stock_consult.no_open_deal')}
                            </p>
                        )}
                        {visibleDeals.map((deal) => {
                            const label = deal.internal_number || deal.deal_number || `#${deal.id}`;
                            const matchable = Boolean(deal.matching_order_product_id);
                            const active = selectedDealId === deal.id;
                            return (
                                <button
                                    key={deal.id}
                                    type="button"
                                    disabled={!matchable}
                                    onClick={() => {
                                        setSelectedDealId(deal.id);
                                        setQty(1);
                                        setShowAllColors(false);
                                        const match = deal.matching_line_color_key
                                            ? colorCards.find((c) => sameColorKey(c.key, deal.matching_line_color_key))
                                            : undefined;
                                        if (match) setColorKey(match.key);
                                    }}
                                    className={cn(
                                        'flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition',
                                        active
                                            ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                            : 'border-gray-200 hover:border-gray-300 dark:border-zinc-700 dark:hover:border-zinc-500',
                                        !matchable && 'cursor-not-allowed bg-gray-50 opacity-60 dark:bg-zinc-900/40',
                                    )}
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-zinc-100">
                                            {label}
                                            <span className="truncate text-xs font-normal text-gray-500">
                                                {deal.contact_name || deal.title}
                                            </span>
                                        </p>
                                        <p className="truncate text-[11px] text-gray-400">
                                            {[deal.owner_name, deal.status_label !== '—' ? deal.status_label : null]
                                                .filter(Boolean)
                                                .join(' · ')}
                                            {matchable && deal.matching_line_qty
                                                ? ` · ${t('v2.stock_consult.deal_line_qty', { qty: deal.matching_line_qty })}`
                                                : ''}
                                        </p>
                                        {!matchable && (
                                            <p className="mt-0.5 text-[11px] text-rose-500">
                                                {deal.has_order === false
                                                    ? t('v2.stock_consult.deal_no_order')
                                                    : t('v2.stock_consult.deal_no_sku')}
                                            </p>
                                        )}
                                    </div>
                                    {active && <PiCheckCircleFill className="mt-0.5 size-4 shrink-0 text-primary" />}
                                </button>
                            );
                        })}
                        {hiddenDeals > 0 && (
                            <p className="text-center text-[11px] text-gray-400">
                                {t('v2.stock_consult.more_deals', { count: hiddenDeals })}
                            </p>
                        )}
                    </div>
                </section>

                <section className="min-w-0 space-y-4">
                    {colorCards.length > 0 && (
                        <div>
                            {stepTitle(2, t('v2.stock_consult.reserve_color'))}
                            {soldColorOutOfStock && (
                                <p className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                                    {t('v2.stock_consult.sold_color_no_stock')}
                                </p>
                            )}
                            <div className="grid grid-cols-1 gap-1.5">
                                {visibleColorCards.map((card) => {
                                    const active = sameColorKey(card.key, colorKey);
                                    const isLine = lineColorCard?.key === card.key;
                                    return (
                                        <button
                                            key={card.key}
                                            type="button"
                                            onClick={() => {
                                                setColorKey(card.key);
                                                setQty(1);
                                            }}
                                            className={cn(
                                                'flex w-full min-w-0 items-center gap-2 rounded-lg border px-3 py-2 text-left text-xs transition',
                                                active
                                                    ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                                    : 'border-gray-200 hover:border-gray-300 dark:border-zinc-700',
                                            )}
                                        >
                                            <span className="flex -space-x-1">
                                                {card.swatches.map((sw) => (
                                                    <span
                                                        key={sw.category}
                                                        className={cn(
                                                            'size-4 rounded-full border-2 border-white dark:border-zinc-800',
                                                            !sw.hex && DOT_STYLE.off,
                                                        )}
                                                        style={dotInlineStyle(sw.hex ? 'standard' : 'off', sw.hex)}
                                                    />
                                                ))}
                                            </span>
                                            <span className="flex min-w-0 flex-1 flex-wrap gap-x-2.5 gap-y-0.5 text-gray-800 dark:text-zinc-100">
                                                {card.swatches.length > 0
                                                    ? card.swatches.map((sw) => (
                                                          <span key={sw.category} className="whitespace-nowrap">
                                                              <span className="text-gray-400 dark:text-zinc-500">{partLabel(sw.category, t)}</span>{' '}
                                                              <span className="font-medium">{sw.name}</span>
                                                          </span>
                                                      ))
                                                    : card.label}
                                            </span>
                                            {isLine && (
                                                <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                                                    {t('v2.stock_consult.sold_color')}
                                                </span>
                                            )}
                                            <span className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">
                                                {card.available}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            {lineColorCard && otherColorsCount > 0 && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (showAllColors && lineColorCard) setColorKey(lineColorCard.key);
                                        setShowAllColors((v) => !v);
                                    }}
                                    className="mt-1.5 text-[11px] font-medium text-primary hover:underline"
                                >
                                    {showAllColors
                                        ? t('v2.stock_consult.only_sold_color')
                                        : t('v2.stock_consult.show_other_colors', { count: otherColorsCount })}
                                </button>
                            )}
                            {colorDiffersFromLine && (
                                <p className="mt-1.5 text-[11px] text-amber-700 dark:text-amber-300">
                                    {t('v2.stock_consult.color_differs_from_line')}
                                </p>
                            )}
                        </div>
                    )}

                    <div>
                        {stepTitle(colorCards.length > 0 ? 3 : 2, t('v2.stock_consult.qty_and_reason'))}
                        <div className="flex items-center gap-2">
                            <Input
                                type="number"
                                min={1}
                                max={maxQty}
                                value={qty}
                                onChange={(e) => setQty(Math.min(maxQty, Math.max(1, Number(e.target.value) || 1)))}
                                className="w-24"
                                aria-label={t('v2.stock_consult.qty')}
                            />
                            <span className="text-[11px] text-gray-400">
                                {t('v2.stock_consult.qty_max', { max: String(maxQty) })}
                            </span>
                        </div>
                        <textarea
                            className="mt-2 min-h-[72px] w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:border-zinc-600 dark:bg-zinc-900"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder={t('v2.stock_consult.reason_placeholder')}
                            aria-label={t('v2.stock_consult.reason')}
                            minLength={3}
                        />
                    </div>
                </section>
            </div>
        </V2Modal>
    );
}

function ProductDetailPanel({
    product,
    onBack,
    showBack,
    t,
    fillViewport = false,
    onViewMap,
}: {
    product: StockConsultProduct;
    onBack?: () => void;
    showBack?: boolean;
    t: (key: string, vars?: Record<string, string | number>) => string;
    fillViewport?: boolean;
    onViewMap?: (shelves: string[]) => void;
}) {
    const [reserveOpen, setReserveOpen] = useState(false);
    const [reserveColorKey, setReserveColorKey] = useState<string | null>(null);
    const canReserve = Boolean(window.__V2__?.can_request_stock_ploomes_deal);
    const canViewLocation = product.can_view_cd_location !== false;
    const openReserve = (colorKey: string | null) => {
        setReserveColorKey(colorKey);
        setReserveOpen(true);
    };
    const colorCards = product.color_cards.filter((c) => c.available > 0 || c.reserved > 0 || c.in_transit > 0);
    const charge = product.totals.total_charge_brl;

    return (
        <div
            className={cn(
                'grid min-h-0 gap-4 lg:grid-cols-[19rem_minmax(0,1fr)] 2xl:grid-cols-[22rem_minmax(0,1fr)]',
                fillViewport && 'h-full overflow-y-auto lg:overflow-hidden',
            )}
        >
            <ReserveFromConsultModal
                open={reserveOpen}
                onClose={() => setReserveOpen(false)}
                product={product}
                t={t}
                initialColorKey={reserveColorKey}
            />

            <aside className={cn('flex min-h-0 flex-col gap-3', fillViewport && 'lg:overflow-y-auto')}>
                {showBack && onBack && (
                    <button
                        type="button"
                        onClick={onBack}
                        className="inline-flex w-fit items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                    >
                        <PiArrowLeftBold className="size-3.5" />
                        {t('v2.stock_consult.back_to_available')}
                    </button>
                )}
                <div className="rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
                    <div className="flex items-center gap-3 lg:block">
                        <div className="flex size-24 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white lg:h-[min(16rem,32vh)] lg:w-full dark:bg-gray-900">
                            {product.image_url ? (
                                <img
                                    src={product.image_url}
                                    alt={product.name}
                                    className="size-full object-contain"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).src = '/img/favicon.png';
                                    }}
                                />
                            ) : (
                                <PiPackageBold className="size-10 text-gray-300 dark:text-gray-600" />
                            )}
                        </div>
                        <div className="min-w-0 lg:mt-3">
                            <Title as="h2" className="text-lg leading-tight lg:text-xl">
                                {product.name}
                            </Title>
                            <p className="mt-0.5 font-mono text-xs text-gray-500">{product.code ?? '—'}</p>
                            <div className="mt-2 flex flex-wrap gap-1">
                                {product.product_type_label && (
                                    <Badge color="primary" size="sm" rounded="pill">
                                        {product.product_type_label}
                                    </Badge>
                                )}
                                {product.category && (
                                    <Badge color="info" size="sm" rounded="pill">
                                        {product.category}
                                    </Badge>
                                )}
                                {product.series && (
                                    <Badge color="warning" size="sm" rounded="pill">
                                        {product.series}
                                    </Badge>
                                )}
                                {product.is_high_turnover && (
                                    <Badge color="info" size="sm" rounded="pill">
                                        {t('v2.stock_consult.high_turnover')}
                                    </Badge>
                                )}
                            </div>
                        </div>
                    </div>

                    <dl className="mt-3 grid grid-cols-2 gap-2">
                        {[
                            { label: t('v2.stock_consult.available'), value: product.totals.available, tone: 'text-emerald-700 dark:text-emerald-300' },
                            { label: t('v2.stock_consult.reserved'), value: product.totals.reserved, tone: 'text-amber-700 dark:text-amber-300' },
                            { label: t('v2.stock_consult.in_transit'), value: product.totals.in_transit, tone: 'text-sky-700 dark:text-sky-300' },
                        ]
                            .filter((s, i) => i === 0 || s.value > 0)
                            .map((s) => (
                                <div key={s.label} className="rounded-lg bg-gray-50 px-2.5 py-1.5 dark:bg-gray-900/50">
                                    <dd className={cn('text-lg font-bold tabular-nums leading-none', s.tone)}>{s.value}</dd>
                                    <dt className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{s.label}</dt>
                                </div>
                            ))}
                        {charge > 0 && (
                            <div className="col-span-2 rounded-lg bg-rose-50 px-2.5 py-1.5 dark:bg-rose-950/30">
                                <dd className="text-sm font-bold tabular-nums text-rose-700 dark:text-rose-300">{formatBrl(charge)}</dd>
                                <dt className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                                    {t('v2.stock_consult.storage_cost_total')}
                                </dt>
                            </div>
                        )}
                    </dl>
                    <p className="mt-2 text-[10px] leading-snug text-gray-400">
                        {t('v2.stock_consult.storage_policy_hint', {
                            free: product.storage_policy.free_days,
                            rate: Number(product.storage_policy.daily_rate_brl).toFixed(2).replace('.', ','),
                        })}
                        {product.cbm != null && (
                            <span className="font-mono"> · {t('v2.stock_consult.cbm')} {Number(product.cbm).toFixed(3)}</span>
                        )}
                    </p>

                    <div className="mt-3 flex flex-col gap-2">
                        {canReserve && product.totals.available > 0 && (
                            <Button size="sm" className="w-full" onClick={() => openReserve(null)}>
                                <PiWarehouseBold className="me-1.5 size-4" />
                                {t('v2.stock_consult.reserve')}
                            </Button>
                        )}
                        <Link
                            to={`/products/${product.id}`}
                            className="inline-flex items-center justify-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                            {t('v2.stock_consult.open_product')}
                            <PiArrowSquareOutBold className="size-3.5" />
                        </Link>
                    </div>
                </div>
            </aside>

            <main className={cn('flex min-h-0 min-w-0 flex-col gap-4', fillViewport && 'lg:overflow-y-auto lg:pe-1')}>
                <MachineComponents components={product.components ?? []} t={t} />

                <section>
                    <h3 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                        {t('v2.stock_consult.combinations_title')}
                        <span className="ms-1.5 font-normal text-gray-400">· {colorCards.length}</span>
                    </h3>
                    {colorCards.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-10 text-center text-sm text-gray-500 dark:border-gray-600">
                            {t('v2.stock_consult.empty_stock')}
                        </div>
                    ) : (
                        <StockCombinations
                            cards={colorCards}
                            t={t}
                            onViewMap={canViewLocation ? onViewMap : undefined}
                            canViewLocation={canViewLocation}
                            onReserve={canReserve ? (key) => openReserve(key) : undefined}
                        />
                    )}
                </section>
            </main>
        </div>
    );
}

function LoadError({ t, onRetry }: { t: (key: string) => string; onRetry: () => void }) {
    return (
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 px-6 py-10 text-center dark:border-rose-500/30 dark:bg-rose-500/10">
            <p className="text-sm font-medium text-rose-700 dark:text-rose-200">{t('v2.stock_consult.load_error')}</p>
            <Button variant="outline" size="sm" onClick={onRetry}>
                {t('v2.stock_consult.retry')}
            </Button>
        </div>
    );
}

function productSkuPath(product: Pick<StockConsultProduct, 'id' | 'code'>): string {
    const sku = (product.code ?? '').trim();
    if (sku !== '') {
        return encodeURIComponent(sku);
    }

    return String(product.id);
}

export default function StockConsultPage() {
    const { t } = useI18n();
    const navigate = useNavigate();
    const { sku: skuParam } = useParams<{ sku?: string }>();
    const selectedSku = useMemo(() => {
        if (!skuParam) {
            return null;
        }
        const decoded = decodeURIComponent(skuParam).trim();
        return decoded !== '' ? decoded : null;
    }, [skuParam]);

    const [search, setSearch] = useState('');
    // Filtro "Phantom Black" etc. pedido na reunião de Supply Chain (cor associada à oportunidade).
    const [colorFilter, setColorFilter] = useState('');
    const [mapShelves, setMapShelves] = useState<string[]>([]);
    const debouncedSearch = useDebouncedValue(search.trim(), 300);
    const canSearch = debouncedSearch.length >= 2;

    const openProduct = (product: Pick<StockConsultProduct, 'id' | 'code'>, replace = false) => {
        navigate(`/estoque/consulta/${productSkuPath(product)}`, { replace });
    };

    const closeProduct = () => {
        navigate('/estoque/consulta');
    };

    // Sair do detalhe durante uma busca: limpa o termo, senão o 1º resultado reabre na hora.
    const closeSearchProduct = () => {
        setSearch('');
        closeProduct();
    };

    /** Último termo que já abriu o 1º resultado sozinho (abre uma vez por termo, sem empilhar histórico). */
    const autoOpenedTerm = useRef<string | null>(null);

    const openOnCdMap = (shelves: string[]) => {
        const codes = shelves.map((s) => s.trim().toUpperCase()).filter(Boolean);
        if (codes.length > 0) {
            setMapShelves(codes);
        }
    };

    const colorOptionsQuery = useQuery({
        queryKey: ['stock-consult', 'color-options'],
        staleTime: 10 * 60 * 1000,
        queryFn: async () => {
            const [frame, seat] = await Promise.all(
                ['frame', 'seat'].map((category) =>
                    api.get<{ data: { name: string; hex: string | null }[] }>('/meta/product-colors', { params: { category } }),
                ),
            );
            const seen = new Map<string, string | null>();
            [...frame.data.data, ...seat.data.data].forEach((c) => {
                if (!seen.has(c.name)) seen.set(c.name, c.hex);
            });
            return [...seen.entries()].map(([name, hex]) => ({ name, hex }));
        },
    });
    const color = colorFilter || undefined;

    const availableQuery = useQuery({
        queryKey: ['stock-consult', 'available', colorFilter],
        enabled: !canSearch,
        queryFn: async () => {
            const { data } = await api.get<{ data: StockConsultProduct[] }>('/stock-consult/available', {
                params: { limit: 24, color },
            });
            return data.data;
        },
    });

    const searchQuery = useQuery({
        queryKey: ['stock-consult', 'search', debouncedSearch, colorFilter],
        enabled: canSearch,
        queryFn: async () => {
            const { data } = await api.get<{ data: StockConsultProduct[] }>('/stock-consult/search', {
                params: { q: debouncedSearch, limit: 20, color },
            });
            return data.data;
        },
    });

    const detailQuery = useQuery({
        queryKey: ['stock-consult', 'product', selectedSku, colorFilter],
        enabled: selectedSku !== null,
        queryFn: async () => {
            const { data } = await api.get<{ data: StockConsultProduct }>(
                `/stock-consult/products/${encodeURIComponent(selectedSku!)}`,
                { params: { color } },
            );
            return data.data;
        },
    });

    const searchResults = searchQuery.data ?? [];
    const availableProducts = availableQuery.data ?? [];

    useEffect(() => {
        if (!canSearch || selectedSku !== null) {
            return;
        }
        if (searchResults.length === 0 || autoOpenedTerm.current === debouncedSearch) {
            return;
        }
        autoOpenedTerm.current = debouncedSearch;
        openProduct(searchResults[0], true);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- só auto-abre o 1º resultado quando a busca muda sem produto na URL
    }, [canSearch, searchResults, selectedSku]);

    const selectedDetail = detailQuery.data ?? null;
    const detailOpen = selectedSku !== null;
    const detailLoading = detailOpen && detailQuery.isLoading;

    const isActiveProduct = (product: Pick<StockConsultProduct, 'id' | 'code'>) => {
        if (selectedSku === null) {
            return false;
        }
        const code = (product.code ?? '').trim().toLowerCase();
        if (code !== '' && code === selectedSku.toLowerCase()) {
            return true;
        }
        return String(product.id) === selectedSku;
    };

    return (
        <div
            className={cn(
                'flex flex-col',
                detailOpen
                    ? 'h-[calc(100dvh-5.25rem)] gap-3 overflow-hidden'
                    : 'min-h-[calc(100dvh-5.25rem)] space-y-6',
            )}
        >
            <CdLocationHighlightModal
                open={mapShelves.length > 0}
                shelves={mapShelves}
                onClose={() => setMapShelves([])}
            />
            {!detailOpen && (
                <PageHeader
                    title={t('v2.stock_consult.title')}
                    subtitle={t('v2.stock_consult.subtitle')}
                    breadcrumb={[
                        { name: t('v2.nav.group.stock') },
                        { name: t('v2.stock_consult.title') },
                    ]}
                >
                    <StockAiSuggestionButton />
                </PageHeader>
            )}

            <div
                className={cn(
                    'shrink-0 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-900',
                    detailOpen && 'py-2.5',
                )}
            >
                <div className="flex flex-col gap-2 sm:flex-row">
                    <div className="relative flex-1">
                        <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-5 -translate-y-1/2 text-gray-400" />
                        <Input
                            type="search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('v2.stock_consult.search_placeholder')}
                            className={cn('ps-11 text-base', detailOpen ? 'h-10' : 'h-12')}
                            autoFocus
                        />
                    </div>
                    <div className="relative sm:w-64">
                        {colorFilter && (
                            <span
                                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 rounded-full ring-1 ring-black/10"
                                style={{
                                    backgroundColor:
                                        colorOptionsQuery.data?.find((c) => c.name === colorFilter)?.hex ?? '#e5e7eb',
                                }}
                            />
                        )}
                        <select
                            value={colorFilter}
                            onChange={(e) => setColorFilter(e.target.value)}
                            aria-label={t('v2.stock_consult.color_filter')}
                            className={cn(
                                'w-full rounded-lg border border-gray-200 bg-white pe-8 text-sm shadow-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:border-zinc-600 dark:bg-zinc-900',
                                colorFilter ? 'ps-9' : 'ps-3',
                                detailOpen ? 'h-10' : 'h-12',
                            )}
                        >
                            <option value="">{t('v2.stock_consult.color_filter_all')}</option>
                            {(colorOptionsQuery.data ?? []).map((c) => (
                                <option key={c.name} value={c.name}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {!canSearch && !detailOpen && (
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{t('v2.stock_consult.search_hint')}</p>
                )}
            </div>

            {canSearch && (
                <div className={cn('min-h-0', detailOpen ? 'flex flex-1 flex-col overflow-hidden' : '')}>
                    {searchQuery.isLoading && <LoadingState variant="cards" />}

                    {searchQuery.isError && <LoadError t={t} onRetry={() => void searchQuery.refetch()} />}

                    {!searchQuery.isLoading && !searchQuery.isError && searchResults.length === 0 && (
                        <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-16 text-center dark:border-gray-600">
                            <PiPackageBold className="mx-auto size-10 text-gray-300 dark:text-gray-600" />
                            <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('v2.stock_consult.empty')}
                            </p>
                        </div>
                    )}

                    {searchResults.length > 0 && (
                        <div
                            className={cn(
                                'grid gap-4 lg:grid-cols-[14rem_minmax(0,1fr)]',
                                detailOpen && 'min-h-0 flex-1 overflow-hidden',
                            )}
                        >
                            <aside className={cn('space-y-2', detailOpen && 'min-h-0 overflow-y-auto pr-1')}>
                                {searchResults.map((product) => {
                                    const active = isActiveProduct(product);
                                    return (
                                        <button
                                            key={product.id}
                                            type="button"
                                            onClick={() => openProduct(product)}
                                            className={cn(
                                                'flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition',
                                                active
                                                    ? 'border-primary bg-primary/5 shadow-sm'
                                                    : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600',
                                            )}
                                        >
                                            <ProductThumbnail src={product.image_url} alt={product.name} size="list" />
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                    {product.name}
                                                </p>
                                                <p className="truncate font-mono text-[11px] text-gray-500">
                                                    {product.code ?? '—'}
                                                </p>
                                                <div className="mt-1 flex flex-wrap gap-1">
                                                    <Badge
                                                        color={product.totals.available > 0 ? 'success' : 'secondary'}
                                                        size="sm"
                                                        rounded="pill"
                                                    >
                                                        <PiWarehouseBold className="me-1 inline size-3" />
                                                        {product.totals.available}
                                                    </Badge>
                                                    {product.totals.in_transit > 0 && (
                                                        <Badge color="info" size="sm" rounded="pill">
                                                            <PiTruckBold className="me-1 inline size-3" />
                                                            {product.totals.in_transit}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </aside>

                            <section className={cn('min-w-0', detailOpen && 'min-h-0 overflow-hidden')}>
                                {detailLoading && <LoadingState variant="detail" />}
                                {!detailLoading && selectedDetail && (
                                    <ProductDetailPanel
                                        product={selectedDetail}
                                        showBack
                                        onBack={closeSearchProduct}
                                        t={t}
                                        fillViewport={detailOpen}
                                        onViewMap={openOnCdMap}
                                    />
                                )}
                                {!detailLoading && detailOpen && detailQuery.isError && (
                                    <LoadError t={t} onRetry={() => void detailQuery.refetch()} />
                                )}
                                {!detailLoading && !selectedDetail && !detailQuery.isError && (
                                    <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-16 text-center text-sm text-gray-500 dark:border-gray-600">
                                        {t('v2.stock_consult.select_product')}
                                    </div>
                                )}
                            </section>
                        </div>
                    )}
                </div>
            )}

            {!canSearch && (
                <>
                    {(availableQuery.isLoading || detailLoading) && !selectedDetail && (
                        <LoadingState variant={detailOpen ? 'detail' : 'cards'} />
                    )}

                    {!detailLoading && selectedDetail && (
                        <div className="min-h-0 flex-1 overflow-hidden">
                            <ProductDetailPanel
                                product={selectedDetail}
                                showBack
                                onBack={closeProduct}
                                t={t}
                                fillViewport
                                onViewMap={openOnCdMap}
                            />
                        </div>
                    )}

                    {!detailOpen && !availableQuery.isLoading && (
                        <div className="space-y-3">
                            <div>
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {t('v2.stock_consult.available_section')}
                                </h2>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {t('v2.stock_consult.available_section_hint')}
                                </p>
                            </div>

                            {availableQuery.isError ? (
                                <LoadError t={t} onRetry={() => void availableQuery.refetch()} />
                            ) : availableProducts.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-16 text-center dark:border-gray-600">
                                    <PiWarehouseBold className="mx-auto size-10 text-gray-300 dark:text-gray-600" />
                                    <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                                        {t('v2.stock_consult.empty_available')}
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                                    {availableProducts.map((product) => (
                                        <AvailableProductCard
                                            key={product.id}
                                            product={product}
                                            onSelect={() => openProduct(product)}
                                            t={t}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {detailOpen && detailQuery.isError && (
                        <div className="rounded-2xl border border-dashed border-gray-300 px-6 py-16 text-center dark:border-gray-600">
                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {(detailQuery.error as { response?: { status?: number } } | null)?.response?.status === 404
                                    ? t('v2.stock_consult.not_found')
                                    : t('v2.stock_consult.load_error')}
                            </p>
                            <div className="mt-4 flex justify-center gap-2">
                                <Button variant="outline" size="sm" onClick={() => void detailQuery.refetch()}>
                                    {t('v2.stock_consult.retry')}
                                </Button>
                                <Button variant="outline" size="sm" onClick={closeProduct}>
                                    <PiArrowLeftBold className="me-1.5 size-4" />
                                    {t('v2.stock_consult.back_to_available')}
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
